import { Button } from '@repo/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { zCleanString } from '@repo/ui/utils'
import { Circle, Info, Loader2, ShieldCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { RouteNames } from '@/routes'
import { storeActions } from '@/store/actions'
import { selectIsAuthenticated } from '@/store/auth/selectors'
import { useStoreDispatch, useStoreState } from '@/store/hooks'
import { passwordSchema } from '@/validation-schemas'

const defaultValues = { email: '', password: '' }

export function LoginRoute() {
  const { t } = useT({ keyPrefix: 'login' })
  const navigate = useNavigate()
  const location = useLocation()

  const isAuthenticated = useSelector(selectIsAuthenticated)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const to = (location.state?.from?.pathname ?? RouteNames.Root) as string
    if (isAuthenticated) {
      navigate(to, { replace: true })
    }
  }, [isAuthenticated])

  return (
    <div className="relative inset-0 z-10 m-auto flex h-full flex-1 flex-col items-center justify-center gap-2">
      <div className="bg-primary-50 border-primary-200 text-primary-600 mb-6 flex w-96 flex-col items-center justify-center gap-2 rounded-lg border p-4 text-center font-semibold">
        <div className="flex gap-2">
          <Info className="h-6 w-6" /> {t('infoBayes')}
        </div>
        <a href="https://caseai.org" className="hover:text-primary underline">
          {t('infoCta')}
        </a>
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>
            <Trans
              i18nKey="login.description"
              components={[<Label className="text-primary">CaseAI</Label>]}
            />
          </CardDescription>
        </CardHeader>
        <LoginForm defaultValues={defaultValues} />
      </Card>
    </div>
  )
}

function LoginForm({ defaultValues }: { defaultValues: { email: string; password: string } }) {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'login' })
  const isLoading = useStoreState((state) => state.user.isLoggingIn)
  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: ({ email, password }) => {
      void dispatch(storeActions.login({ email, password }))
    },
    schema: {
      email: zCleanString.email(),
      password: passwordSchema,
    },
  })

  return (
    <Form {...form}>
      <form onSubmit={submit} onKeyDown={submitOnPressMetaEnter}>
        <CardContent className="grid gap-2">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tc('email')}</FormLabel>
                <FormControl>
                  <Input placeholder="m@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tc('password')}</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="••••••••••••••" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
        <CardFooter className="flex flex-col gap-2 pb-2">
          <Button className="w-full" type="submit" disabled={!isFormValid || isLoading}>
            <div>
              {isFormValid ? (
                <>
                  {isLoading ? (
                    <Loader2 className="size-5 animate-spin" />
                  ) : (
                    <ShieldCheck className="size-5" />
                  )}
                </>
              ) : (
                <Circle className="size-5 opacity-50" />
              )}
            </div>
            {t(isLoading ? 'loading' : 'signin', { cfl: true })}
          </Button>

          <ForgotPassword />
        </CardFooter>
      </form>
    </Form>
  )
}

function ForgotPassword() {
  const { t } = useT({ keyPrefix: 'login' })
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  return (
    <>
      <Button
        variant="link"
        className="text-primary-800 hover:text-primary transition-colors"
        onClick={() => setShowForgotPassword(true)}
      >
        {t('forgotPassword')}
      </Button>
      {showForgotPassword && (
        <ForgotPasswordDialog
          modalHandler={{
            open: showForgotPassword,
            setOpen: setShowForgotPassword,
          }}
        />
      )}
    </>
  )
}

function ForgotPasswordDialog({
  modalHandler,
}: {
  modalHandler: { open: boolean; setOpen: (open: boolean) => void }
}) {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'login' })
  const forgotPasswordValues = useMemo(() => ({ email: '' }), [])
  const {
    form: forgotPasswordForm,
    isFormValid: isForgotPasswordFormValid,
    submit: submitForgotPassword,
  } = useFormValidator({
    defaultValues: forgotPasswordValues,
    onSubmit: ({ email }) => {
      void dispatch(storeActions.user.requestPasswordReset({ email }))
      modalHandler.setOpen(false)
    },
    schema: { email: zCleanString.email() },
  })
  return (
    <Dialog open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('forgotPasswordTitle')}</DialogTitle>
          <DialogDescription>{t('forgotPasswordDescription')}</DialogDescription>
        </DialogHeader>

        <Form {...forgotPasswordForm}>
          <form onSubmit={submitForgotPassword} className="space-y-4">
            <FormField
              control={forgotPasswordForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('email')}</FormLabel>
                  <FormControl>
                    <Input placeholder="m@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex justify-between sm:justify-between">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t('cancel')}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={!isForgotPasswordFormValid}>
                {t('sendResetLink')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
